import axios from 'axios';

/**
 * REACT_APP_API_URL is sometimes set to http://SERVER_IP:5000/api for local testing.
 * Gunicorn only listens on 127.0.0.1:5000, so browsers on the internet get
 * ERR_CONNECTION_TIMED_OUT. Same for localhost/127.0.0.1 in the URL.
 * In those cases use same-origin /api so Nginx can proxy to the backend.
 */
const envApiUrlWouldTimeoutFromBrowser = (raw) => {
  if (!raw || typeof window === 'undefined') return false;
  try {
    const u = new URL(raw.trim(), window.location.origin);
    const h = u.hostname.toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]') return true;
    if (u.port === '5000') return true;
    return false;
  } catch {
    return false;
  }
};

// Auto-detect API URL based on current hostname
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim();

  if (envUrl && envApiUrlWouldTimeoutFromBrowser(envUrl)) {
    console.warn(
      '[api] REACT_APP_API_URL points at localhost or :5000; using same-origin /api (use Nginx in production).'
    );
    return '/api';
  }

  if (envUrl) {
    return envUrl;
  }

  const hostname = window.location.hostname;
  const isProduction =
    process.env.NODE_ENV === 'production' ||
    (hostname !== 'localhost' && hostname !== '127.0.0.1');

  if (isProduction) {
    return '/api';
  }

  const port = process.env.REACT_APP_API_PORT || '5000';
  return `http://localhost:${port}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // For FormData, remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.error || '';
    
    // Handle 401 - Always log out
    if (status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    } 
    // Handle 403 - Only log out if it's an authentication issue
    else if (status === 403) {
      // Only log out if it's an authentication-related 403, not a permission issue
      if (errorMessage.includes('token') || 
          errorMessage.includes('Authentication') || 
          errorMessage.includes('expired') ||
          errorMessage.includes('User not found')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      // Otherwise, it's a permission issue - don't log out, just show the error
    }
    // Handle 503 - Service Unavailable (connection pool exhausted)
    // Don't log out, just show error message
    else if (status === 503) {
      // Don't log out for service unavailable errors
      console.warn('Service temporarily unavailable:', errorMessage);
    }
    
    return Promise.reject(error);
  }
);

export const uploadKML = async (file, projectName, layerName) => {
  const formData = new FormData();
  formData.append('kmlFile', file);
  formData.append('name', projectName);
  if (layerName) {
    formData.append('layerName', layerName);
  }

  // Use api instance to include authentication token
  // The interceptor will handle removing Content-Type for FormData
  const response = await api.post('/upload/kml', formData);
  return response.data;
};

export const addKmlToProject = async (projectId, file, layerName, folderId) => {
  const formData = new FormData();
  formData.append('kmlFile', file);
  if (layerName) formData.append('layerName', layerName);
  if (folderId != null) formData.append('folderId', String(folderId));
  const response = await api.post(`/projects/${projectId}/kml`, formData);
  return response.data;
};

export const getProjects = async () => {
  const response = await api.get('/projects');
  return response.data;
};

export const getProject = async (projectId) => {
  const response = await api.get(`/projects/${projectId}`);
  return response.data;
};

export const updateProjectName = async (projectId, name) => {
  const response = await api.patch(`/projects/${projectId}`, { name });
  return response.data;
};

export const getProjectStats = async (projectId, opts = {}) => {
  const params = {};
  if (opts.kmlLayerIds != null) {
    params.kmlLayerIds = opts.kmlLayerIds.length ? opts.kmlLayerIds.join(',') : '';
  }
  const response = await api.get(`/projects/${projectId}/stats`, { params });
  return response.data;
};

export const updateBlockStatus = async (blockId, statuses) => {
  const response = await api.put(`/blocks/${blockId}/status`, statuses);
  return response.data;
};

export const deleteProject = async (projectId) => {
  const response = await api.delete(`/projects/${projectId}`);
  return response.data;
};

// Authentication
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/auth/change-password', { currentPassword, newPassword });
  return response.data;
};

// User Management (Admin only)
export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

export const getUserProjects = async (userId) => {
  const response = await api.get(`/users/${userId}/projects`);
  return response.data;
};

export const assignUserProjects = async (userId, projectIds) => {
  const response = await api.post(`/users/${userId}/projects`, { projectIds });
  return response.data;
};

// Project manage (folders, layers, memberships)
export const createProjectFolder = async (projectId, body) => {
  const response = await api.post(`/projects/${projectId}/folders`, body);
  return response.data;
};

export const updateProjectFolder = async (projectId, folderId, body) => {
  const response = await api.patch(`/projects/${projectId}/folders/${folderId}`, body);
  return response.data;
};

export const deleteProjectFolder = async (projectId, folderId) => {
  const response = await api.delete(`/projects/${projectId}/folders/${folderId}`);
  return response.data;
};

export const updateKmlLayer = async (projectId, layerId, body) => {
  const response = await api.patch(`/projects/${projectId}/layers/${layerId}`, body);
  return response.data;
};

export const deleteKmlLayer = async (projectId, layerId) => {
  const response = await api.delete(`/projects/${projectId}/layers/${layerId}`);
  return response.data;
};

export const downloadKmlLayer = async (projectId, layerId, filename) => {
  const response = await api.get(`/projects/${projectId}/layers/${layerId}/kml`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'layer.kml');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getProjectAssignments = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/assignments`);
  return response.data;
};

export const putProjectAssignments = async (projectId, userIds) => {
  const response = await api.put(`/projects/${projectId}/assignments`, { userIds });
  return response.data;
};

export const getAssignmentEligibleUsers = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/assignment-eligible-users`);
  return response.data;
};

// Activity log (admin)
export const getActivityLogs = async (params = {}) => {
  const q = {
    page: params.page || 1,
    perPage: params.perPage || 25
  };
  if (params.userId != null && params.userId !== '') {
    q.userId = params.userId;
  }
  const response = await api.get('/activity', { params: q });
  return response.data;
};

// Status Details
export const getBlockStatusDetails = async (blockId) => {
  const response = await api.get(`/blocks/${blockId}/status-details`);
  return response.data;
};

export const getBulkBlockStatusDetails = async (blockIds) => {
  // blockIds should be a comma-separated string like "1,2,3"
  const response = await api.get(`/blocks/bulk/status-details?block_ids=${blockIds}`);
  return response.data;
};

// Backup & Restore
export const downloadBackup = async () => {
  const response = await api.get('/backup/export', {
    responseType: 'blob'
  });
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'drone_mapping_backup.json';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const restoreBackup = async (file, clearExisting = false) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('clear_existing', clearExisting.toString());
  
  const response = await api.post('/backup/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const saveBlockStatusDetails = async (blockId, statusType, data) => {
  const response = await api.post(`/blocks/${blockId}/status-details`, { statusType, ...data });
  return response.data;
};

export const uploadStatusAttachment = async (blockId, statusType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(
    `/blocks/${blockId}/status-details/${statusType}/attachments`,
    formData
  );
  return response.data;
};

export const downloadStatusAttachment = async (attachmentId, filename) => {
  const response = await api.get(`/blocks/attachments/${attachmentId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'download');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteStatusAttachment = async (attachmentId) => {
  const response = await api.delete(`/blocks/attachments/${attachmentId}`);
  return response.data;
};

// Report Download
export const downloadProjectReport = async (projectId, format = 'pdf', options = {}) => {
  const params = { format };
  if (options.kmlLayerIds != null) {
    params.kmlLayerIds = options.kmlLayerIds.length ? options.kmlLayerIds.join(',') : '';
  }
  if (options.showBlockLabels !== undefined) {
    params.showBlockLabels = options.showBlockLabels ? '1' : '0';
  }
  const response = await api.get(`/projects/${projectId}/report`, {
    params,
    responseType: format === 'html' ? 'text' : 'blob'
  });
  return response.data;
};

export default api;

